import {AlertCircle, ArrowLeft, Clock, ExternalLink, Rocket, Send, Users} from 'lucide-react'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { WalletAddressContext } from '@/lib/walletContext'
import BondingCurveCalculator from '@/components/BondingCurveCalculator'
import CurvePriceChart from '@/components/CurvePriceChart'
import FundingProgress from '@/components/FundingProgress'
import Layout from '@/components/Layout'
import PresaleHolders from '@/components/PresaleHolders'
import PresalePledgeBox from '@/components/PresalePledgeBox'
import PresaleSettleBox from '@/components/PresaleSettleBox'
import QuickLaunchTrade from '@/components/QuickLaunchTrade'
import SeoHead from '@/components/SeoHead'
import { tokenShareUrl } from '@/lib/shareSlug'
import StatusBadge from '@/components/StatusBadge'
import TokenomicsPanel from '@/components/TokenomicsPanel'
import WatchButton from '@/components/WatchButton'
import ColonyFeed from '@/components/ColonyFeed'
import NewsFeed from '@/components/NewsFeed'
import RecentTrades from '@/components/RecentTrades'
import ExplorerLink from '@/components/ExplorerLink'
import PresaleEndsCountdown from '@/components/PresaleEndsCountdown'
import ShareTokenLink from '@/components/ShareTokenLink'
import TokenContractAddress from '@/components/TokenContractAddress'
import TokenImageEditor from '@/components/TokenImageEditor'
import TokenPageBanner from '@/components/TokenPageBanner'
import TokenSocialLinks from '@/components/TokenSocialLinks'
import TokenSocialLinksEditor from '@/components/TokenSocialLinksEditor'
import { useProjectLinks, type ProjectLinkRecord } from '@/lib/projectLinks'
import { projectToTokenSocials } from '@/lib/tokenSocials'
import { priceAtSupply } from '@/lib/bondingCurve'
import { getBondingInfo } from '@/lib/bondingProgress'
import { addColonyActivity, getTokenBalance } from '@/lib/colonyActivity'
import { formatCurrency, formatNumber, formatQuoteAmount, getProjectCountdownLabel } from '@/lib/format'
import { getProjectFeed } from '@/lib/colonyFeed'
import { useSimLaunch } from '@/hooks/useSimLaunchpad'
import { useSimPresale } from '@/hooks/useSimPresales'
import { isEthAddress } from '@/lib/launchpad/contracts'
import { getProjectById } from '@/lib/mockData'
import { generateCurveHistory } from '@/lib/priceHistory'
import { isPresaleLaunched } from '@/lib/presale/math'
import { simPresaleToProject } from '@/lib/presale/toProject'
import { queueLaunchVerification } from '@/lib/verifyLaunch'
import {
  clampPresaleLpPct,
  creatorPctFromLp,
  formatBps,
  PRESALE_FEE_BPS,
  PRESALE_LAUNCH_FEE_ETH,
  PRESALE_LP_DEFAULT_PCT,
} from '@/lib/protocol'
import ErrorBoundary from '@/components/ErrorBoundary'
import SimLaunchDetail from '@/pages/SimLaunchDetail'

function NotFoundBlock() {
  return (
    <Layout>
      <SeoHead title="Project Not Found" description="This Penguin Pad project could not be found." />
      <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center sm:px-6">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-900 text-zinc-500">
          <AlertCircle className="h-6 w-6" aria-hidden="true" />
        </div>
        <h1 className="mt-4 text-xl font-bold text-white">Project not found</h1>
        <p className="mt-2 text-sm text-zinc-400">This project may have been removed or the link is incorrect.</p>
        <Link to="/launchpad" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-lime-400 px-4 py-2 text-sm font-semibold text-black">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Launchpad
        </Link>
      </div>
    </Layout>
  )
}

function ProjectDetail() {
  const { id, slug } = useParams<{ id?: string, slug?: string }>()
  const tokenKey = id || slug
  const address = useContext(WalletAddressContext)
  const simLaunch = useSimLaunch(tokenKey)
  const simPresale = useSimPresale(tokenKey)
  const project = simPresale
    ? simPresaleToProject(simPresale)
    : (tokenKey ? getProjectById(tokenKey) : undefined)
  const projectKeys = [simPresale?.contractAddress, simPresale?.shareSlug, simPresale?.id, simPresale?.tokenAddress, project?.shareSlug, project?.id, project?.contractAddress, project?.tokenAddress]
  const publishedLinks = useProjectLinks(
    projectKeys,
    project
      ? { website: project.website, image: project.imageUrl, socials: projectToTokenSocials(project) }
      : undefined,
    simPresale || project?.type === 'presale' ? 'presale' : 'curve',
  )
  const [editedLinks, setEditedLinks] = useState<ProjectLinkRecord | null>(null)
  const links = {
    website: editedLinks?.website ?? publishedLinks.website ?? project?.website,
    image: editedLinks?.image ?? publishedLinks.image ?? project?.imageUrl,
    banner: editedLinks?.banner ?? publishedLinks.banner,
    socials: { ...projectToTokenSocials(project), ...publishedLinks.socials, ...editedLinks?.socials },
  }
  const [contribution, setContribution] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [contributionError, setContributionError] = useState<string | null>(null)
  const [liveSupply, setLiveSupply] = useState(project?.supply ?? 0)

  useEffect(() => {
    setLiveSupply(project?.supply ?? 0)
  }, [project?.id, project?.supply])

  useEffect(() => {
    const address = simPresale?.contractAddress
    if (!address)
      return
    queueLaunchVerification({
      kind: 'presale',
      address,
      token: simPresale?.tokenAddress,
    })
  }, [simPresale?.contractAddress, simPresale?.tokenAddress])

  const isLaunchpad = project?.type === 'launchpad'
  const soldOutLaunched = Boolean(simPresale && isPresaleLaunched(simPresale))
  const accent = isLaunchpad ? 'lime' : 'orange'

  const currentPrice = useMemo(() => {
    if (!project || !isLaunchpad)
      return 0
    return priceAtSupply(project.basePrice ?? 0, project.priceIncrement ?? 0, liveSupply)
  }, [project, isLaunchpad, liveSupply])

  const bondingInfo = useMemo(() => {
    if (!project)
      return { progressPct: 0, isBonded: false }
    return getBondingInfo({ ...project, supply: liveSupply })
  }, [project, liveSupply])

  const curveHistory = useMemo(() => {
    if (!project || !isLaunchpad)
      return []
    return generateCurveHistory(project.id, project.basePrice ?? 0, project.priceIncrement ?? 0, liveSupply)
  }, [project, isLaunchpad, liveSupply])

  if (simLaunch || (tokenKey && isEthAddress(tokenKey) && !simPresale)) {
    return (
      <ErrorBoundary>
        <SimLaunchDetail id={simLaunch?.id || tokenKey || ''} />
      </ErrorBoundary>
    )
  }

  if (!project)
    return <NotFoundBlock />

  const cap = isLaunchpad ? project.goal : (project.hardCap ?? project.goal)
  const backPath = isLaunchpad ? '/launchpad' : '/presale'
  const backLabel = isLaunchpad ? 'Launchpad' : 'Presale'
  const accentText = isLaunchpad ? 'text-lime-400' : 'text-orange-400'
  const accentBg = isLaunchpad ? 'bg-lime-400' : 'bg-orange-400'
  const accentBorder = isLaunchpad ? 'border-lime-400/30 focus:border-lime-400/50 focus:ring-lime-400/30' : 'border-orange-400/30 focus:border-orange-400/50 focus:ring-orange-400/30'

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const amount = Number.parseFloat(contribution) || 0
    const min = project?.minContribution ?? 0
    const max = project?.maxContribution ?? Infinity
    if (amount <= 0) {
      setContributionError('Enter a contribution amount greater than 0.')
      setSubmitted(false)
      return
    }
    if (amount < min) {
      setContributionError(`Minimum contribution is ${formatCurrency(min)}.`)
      setSubmitted(false)
      return
    }
    if (amount > max) {
      setContributionError(`Maximum contribution is ${formatCurrency(max)}.`)
      setSubmitted(false)
      return
    }
    setContributionError(null)
    addColonyActivity({
      kind: 'contribute',
      projectId: project?.id,
      projectName: project?.name ?? 'Presale',
      ticker: project?.ticker ?? 'TOKEN',
      summary: `Contributed ${formatCurrency(amount)} to ${project?.name ?? 'presale'}`,
      amountUsd: amount,
    }, address)
    setSubmitted(true)
  }

  return (
    <Layout>
      <ErrorBoundary>
      <SeoHead
        title={`${project.name} (${project.ticker})`}
        description={project.tagline}
        image={links.banner}
        imageAlt={`${project.name} ($${project.ticker}) banner`}
        url={tokenShareUrl(project.shareSlug, project.id)}
      />

      <section>
        <div className="mx-auto max-w-7xl px-4 pb-2 pt-10 sm:px-6 lg:px-8">
          <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to
            {' '}
            {backLabel}
          </Link>
          {soldOutLaunched
            ? (
                <div className="mt-5 rounded-3xl border border-orange-400/40 bg-orange-400 px-6 py-5 text-black">
                  <p className="text-2xl font-bold uppercase tracking-[0.12em] sm:text-4xl">Congrats! The Presale Sold-Out!</p>
                  <p className="mt-2 text-sm font-medium sm:text-base">You can now buy on Uniswap.</p>
                </div>
              )
            : null}
          <TokenPageBanner
            name={project.name}
            banner={links.banner}
            projectKeys={projectKeys}
            kind={isLaunchpad ? 'curve' : 'presale'}
            creatorAddress={project.creatorAddress}
            accent={isLaunchpad ? 'lime' : 'orange'}
            onPublished={banner => setEditedLinks(prev => ({ ...prev, banner, image: prev?.image }))}
          />

          <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="relative h-14 w-14 shrink-0">
                {links.image
                  ? (
                      <img
                        src={links.image}
                        alt={`${project.name} token art`}
                        className="h-14 w-14 rounded-2xl object-cover"
                      />
                    )
                  : (
                      <div
                        className={`flex h-14 w-14 items-center justify-center rounded-2xl border text-base font-bold ${
                          isLaunchpad
                            ? 'border-lime-400/30 bg-lime-400/10 text-lime-400'
                            : 'border-orange-400/30 bg-orange-400/10 text-orange-400'
                        }`}
                        aria-hidden="true"
                      >
                        {(project.ticker || 'PP').slice(0, 2)}
                      </div>
                    )}
                <TokenImageEditor
                  projectKeys={projectKeys}
                  kind={isLaunchpad ? 'curve' : 'presale'}
                  creatorAddress={project.creatorAddress}
                  currentImage={links.image}
                  accent={isLaunchpad ? 'lime' : 'orange'}
                  onPublished={image => setEditedLinks(prev => ({ ...prev, image, banner: prev?.banner }))}
                />
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-2xl font-bold text-white sm:text-3xl">{project.name}</h1>
                  <StatusBadge status={project.status} />
                </div>
                <div className="mt-2">
                  <TokenContractAddress
                    address={project.tokenAddress || project.contractAddress}
                    accent={isLaunchpad ? 'lime' : 'orange'}
                  />
                </div>
                <p className="mt-1 text-sm text-zinc-400">
                  $
                  {project.ticker}
                  {' '}
                  &middot;
                  {' '}
                  {project.category}
                  {' '}
                  &middot;
                  {' '}
                  {project.tagline}
                </p>
                {project.contractAddress || project.tokenAddress
                  ? (
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {project.contractAddress
                          ? (
                              <ExplorerLink
                                address={project.contractAddress}
                                label="View contract on Blockscout"
                                className={`inline-flex items-center gap-1 text-xs font-medium ${
                                  isLaunchpad ? 'text-lime-400 hover:text-lime-300' : 'text-orange-400 hover:text-orange-300'
                                }`}
                              />
                            )
                          : null}
                        {!isLaunchpad && project.tokenAddress
                          ? (
                              <ExplorerLink
                                address={project.tokenAddress}
                                label="View token on Blockscout"
                                className="inline-flex items-center gap-1 text-xs font-medium text-orange-400 hover:text-orange-300"
                              />
                            )
                          : null}
                      </div>
                    )
                  : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <ShareTokenLink shareSlug={project.shareSlug} fallbackId={project.id} accent={isLaunchpad ? 'lime' : 'orange'} />
              <WatchButton projectId={project.id} projectName={project.name} />
              {(links.website || project.website) && (
                <a href={links.website || project.website} target="_blank" rel="noopener noreferrer" aria-label={`${project.name} website`} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:border-white/20 hover:text-white">
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
          <div className="mt-6">
            <TokenSocialLinksEditor
              projectKeys={projectKeys}
              kind={isLaunchpad ? 'curve' : 'presale'}
              creatorAddress={project.creatorAddress}
              initial={links}
              accent={isLaunchpad ? 'lime' : 'orange'}
              onPublished={record => setEditedLinks(prev => ({ ...prev, ...record, image: record.image || prev?.image, banner: record.banner || prev?.banner }))}
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
              <h2 className="text-sm font-semibold text-white">About</h2>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{project.description}</p>
              <TokenSocialLinks
                name={project.name}
                website={links.website}
                socials={links.socials}
                accent={isLaunchpad ? 'lime' : 'orange'}
                embedded
              />
            </div>

            {isLaunchpad && curveHistory.length > 0 && (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-white">Bonding curve</h2>
                  {bondingInfo.isBonded
                    ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-lime-400/30 bg-lime-400/10 px-2.5 py-1 text-xs font-medium text-lime-400">
                          <Rocket className="h-3.5 w-3.5" aria-hidden="true" />
                          Bonded — trading on open market
                        </span>
                      )
                    : (
                        <span className="text-xs text-zinc-500">
                          {bondingInfo.progressPct.toFixed(0)}
                          % to bonding
                        </span>
                      )}
                </div>
                <div className="mt-4">
                  <CurvePriceChart points={curveHistory} accent="lime" />
                </div>
                <div className="mt-4">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-900" role="progressbar" aria-valuenow={Math.round(bondingInfo.progressPct)} aria-valuemin={0} aria-valuemax={100} aria-label="Bonding progress">
                    <div className="h-full rounded-full bg-lime-400 transition-all" style={{ width: `${bondingInfo.progressPct}%` }} />
                  </div>
                  <p className="mt-2 text-[11px] text-zinc-500">
                    Once the curve fills, liquidity graduates and $
                    {project.ticker}
                    {' '}
                    bonds to open trading.
                  </p>
                </div>
              </div>
            )}

            {isLaunchpad && !bondingInfo.isBonded && project.status === 'live' && (
              <QuickLaunchTrade
                key={project.id}
                projectId={project.id}
                projectName={project.name}
                ticker={project.ticker}
                basePrice={project.basePrice ?? 0.01}
                increment={project.priceIncrement ?? 0.000005}
                supply={liveSupply}
                holderBalance={getTokenBalance(project.id, address)}
                onSupplyChange={setLiveSupply}
              />
            )}

            {isLaunchpad && (
              <BondingCurveCalculator />
            )}

            <TokenomicsPanel project={project} />

            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
              <h2 className="text-sm font-semibold text-white">
                {isLaunchpad ? 'Curve stats' : 'Raise terms'}
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {isLaunchpad
                  ? (
                      <>
                        <Stat label="Current price" value={formatCurrency(currentPrice)} accent={accentText} />
                        <Stat label="Market cap" value={formatCurrency(project.marketCap ?? 0)} />
                        <Stat label="Supply" value={formatNumber(liveSupply)} />
                        <Stat label="Max supply" value={formatNumber(project.maxSupply ?? 0)} />
                      </>
                    )
                  : (
                      <>
                        <Stat label="Soft cap" value={formatQuoteAmount(project.softCap ?? 0, project.quote)} />
                        <Stat label="Hard cap" value={formatQuoteAmount(project.hardCap ?? 0, project.quote)} accent={accentText} />
                        <Stat label="Min contribution" value={formatQuoteAmount(project.minContribution ?? 0, project.quote)} />
                        <Stat label="Max contribution" value={formatQuoteAmount(project.maxContribution ?? 0, project.quote)} />
                        <Stat label="Tokens per ETH" value={formatNumber(project.tokensPerEth ?? 0)} accent={accentText} />
                        <Stat label="Listing rate" value={formatNumber(project.listingRate ?? project.tokensPerEth ?? 0)} />
                        <Stat label="Launch fee" value={`${PRESALE_LAUNCH_FEE_ETH} ETH`} />
                        <Stat label="Platform fee" value={formatBps(PRESALE_FEE_BPS)} />
                        <Stat
                          label="LP share"
                          value={`${clampPresaleLpPct(project.lpPct ?? PRESALE_LP_DEFAULT_PCT)}%`}
                          accent={accentText}
                        />
                        <Stat
                          label="Creator share"
                          value={`${creatorPctFromLp(project.lpPct ?? PRESALE_LP_DEFAULT_PCT)}%`}
                        />
                      </>
                    )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
              <FundingProgress raised={project.raised} goal={cap} accent={accent} unit={project.quote === 'eth' ? 'eth' : 'usd'} />

              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4 text-xs text-zinc-500">
                <span className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" aria-hidden="true" />
                  {formatNumber(project.participants)}
                  {' '}
                  participants
                </span>
                {!isLaunchpad
                  ? (
                      <PresaleEndsCountdown
                        endsAt={project.endsAt}
                        startsAt={project.startsAt}
                        status={project.status}
                        withIcon
                        className={`flex items-center gap-1.5 ${project.status === 'upcoming' ? 'font-semibold text-lime-400' : 'font-medium text-orange-300'}`}
                      />
                    )
                  : (() => {
                      const countdownLabel = getProjectCountdownLabel(project)
                      if (!countdownLabel)
                        return null
                      const isUpcomingLiveIn = project.status === 'upcoming'
                      return (
                        <span className={`flex items-center gap-1.5 ${isUpcomingLiveIn ? 'font-semibold text-lime-400' : ''}`}>
                          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                          {countdownLabel}
                        </span>
                      )
                    })()}
              </div>

              {soldOutLaunched
                ? (
                    <div className="mt-5 rounded-2xl border border-orange-400/30 bg-orange-400/10 p-3 text-center text-xs font-medium text-orange-200">
                      Congrats! The Presale Sold-Out! You can now buy on Uniswap.
                    </div>
                  )
                : project.status === 'live'
                ? (
                    isLaunchpad
                      ? (
                          <div className="mt-5 rounded-lg border border-lime-400/20 bg-lime-400/5 p-3 text-center text-xs text-lime-400">
                            Use the Quick launch trading panel to buy or sell $
                            {project.ticker}
                            {' '}
                            on the live curve.
                          </div>
                        )
                      : (
                          simPresale
                            ? <PresalePledgeBox presale={simPresale} />
                            : (
                          <form onSubmit={handleSubmit} className="mt-5 space-y-3 border-t border-white/10 pt-5">
                            <label htmlFor="contribution-amount" className="block text-xs font-medium text-zinc-400">
                              Contribution amount (USD)
                            </label>
                            <input
                              id="contribution-amount"
                              type="number"
                              min={project.minContribution ?? 0}
                              max={project.maxContribution ?? undefined}
                              step="any"
                              required
                              value={contribution}
                              onChange={(e) => { setContribution(e.target.value); setContributionError(null) }}
                              placeholder="100"
                              aria-invalid={!!contributionError}
                              aria-describedby={contributionError ? 'contribution-error' : undefined}
                              className={`w-full rounded-xl border bg-black/50 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 ${accentBorder}`}
                            />
                            <button
                              type="submit"
                              className={`flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-black transition-colors ${accentBg} hover:opacity-90`}
                            >
                              <Send className="h-4 w-4" aria-hidden="true" />
                              Contribute
                            </button>
                            {contributionError && (
                              <p id="contribution-error" className="text-center text-xs text-red-400" role="alert">
                                {contributionError}
                              </p>
                            )}
                            {submitted && !contributionError && (
                              <p className="text-center text-xs text-lime-400" role="status">
                                Simulated contribution submitted — no real funds moved.
                              </p>
                            )}
                          </form>
                            )
                        )
                  )
                : (
                    <div className="mt-5 rounded-2xl border border-white/10 bg-black/40 p-3 text-center text-xs text-zinc-500">
                      This project is not currently accepting
                      {' '}
                      {isLaunchpad ? 'purchases' : 'contributions'}
                      .
                    </div>
                  )}
              {!isLaunchpad && simPresale ? <PresaleSettleBox presale={simPresale} /> : null}
            </div>

            {!isLaunchpad && simPresale ? <PresaleHolders presale={simPresale} /> : null}

            {(project.contractAddress || project.tokenAddress)
              ? (
                  <NewsFeed
                    projectKey={project.contractAddress || project.tokenAddress || project.id}
                    kind={isLaunchpad ? 'curve' : 'presale'}
                    tokenAddress={project.tokenAddress || project.contractAddress || ''}
                    accent={isLaunchpad ? 'lime' : 'orange'}
                  />
                )
              : null}

            <RecentTrades
              projectKey={project.contractAddress || project.tokenAddress || project.id}
              kind={isLaunchpad ? 'curve' : 'presale'}
              accent={isLaunchpad ? 'lime' : 'orange'}
            />

            <ColonyFeed items={getProjectFeed(project.id, 6)} title="Recent colony activity" emptyLabel="No trades for this project yet." />
          </div>
        </div>
      </section>
      </ErrorBoundary>
    </Layout>
  )
}

function Stat({ label, value, accent }: { label: string, value: string, accent?: string }) {
  return (
    <div>
      <p className="text-xs text-zinc-500">{label}</p>
      <p className={`mt-1 text-base font-bold ${accent ?? 'text-white'}`}>{value}</p>
    </div>
  )
}

export default ProjectDetail
